/**
 * Runs, schedules, files and settings pages (prompt.md §37, §54, §55).
 *
 * Each page is deliberately thin: the shared `react-ui` components own the
 * presentation rules and `react-app-core` owns the data, so these files only wire
 * the two together. That is what lets the Tauri shell mount the same screens.
 */

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FileArtifactList,
  MemoryList,
  RunList,
  ScheduleList,
  Select,
  Spinner,
  StorageRouteCard,
  Switch,
  TextArea,
  TextField,
  UsagePanel,
  WorkspaceSwitcher,
  rowStyle,
  stackStyle,
  MUTED_TEXT,
} from "@dayvise/react-ui";
import type { ScheduleId } from "@dayvise/domain";
import { entitlementAllowed } from "@dayvise/client-sdk";
import {
  useAdminMembers,
  useAdminMutations,
  useAdminSharedMemories,
  useAgentMutations,
  useAgents,
  useAuditLog,
  BillingPanel,
  ToolPermissionsPanel,
  McpServersPanel,
  SkillsPanel,
  CallPreferencesPanel,
  AutomationTemplatesPanel,
  BoardsSection,
  MemoryGraphPanel,
  CodexRuntimeCard,
  DefaultWorkspaceCard,
  SkillBundlesPanel,
  SkillExportButton,
  SnapshotsPanel,
  WebPushSettingsCard,
  YoloModePanel,
  useCreateAgent,
  useCredentialMutations,
  useCredentials,
  useEntitlements,
  useFiles,
  useFileDownload,
  useMcpServerMutations,
  useMcpServers,
  useMemory,
  useMemoryMutations,
  useNotifications,
  InputRequestInboxSection,
  useRefinementMutations,
  useRefinements,
  useRuns,
  useScheduleMutations,
  useSchedules,
  useSession,
  useSkillMutations,
  useSkills,
  useStorageMigrationJob,
  useStorageMutations,
  useStorageRoute,
  useUsage,
} from "@dayvise/react-app-core";

function useErrorText(error: unknown, fallback: string): string | null {
  if (error === null || error === undefined) return null;
  return error instanceof Error ? error.message : fallback;
}

/* ---------------------------------- runs ---------------------------------- */

export function RunsPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const runs = useRuns();
  const error = useErrorText(runs.error, "could not load runs");

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Runs</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        A run continues in the cloud whether or not this tab is open. Open its conversation to watch it and to steer it.
      </p>
      {runs.isPending ? <Spinner label="Loading runs" /> : null}
      {error ? <p style={{ color: "var(--dayvise-color-danger)" }}>{error}</p> : null}
      <RunList
        runs={runs.data?.runs ?? []}
        onOpenConversation={(run) => {
          void navigate({ to: "/c/$conversationId", params: { conversationId: run.conversationId } });
        }}
      />
      <Card title="Active run policy" subtitle={`${session.workspace?.plan ?? "unknown"} plan`}>
        <p style={{ margin: 0, fontSize: "var(--dayvise-font-size-sm)" }}>
          Parallel runs are allowed across conversations and within one conversation: each run writes its own branch, so two runs
          never overwrite each other's history.
        </p>
        {session.workspace ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void navigate({ to: "/", search: undefined as never })}
          >
            Go to conversations
          </Button>
        ) : null}
      </Card>
    </div>
  );
}

/* --------------------------------- agents --------------------------------- */

export function AgentsPage() {
  const agents = useAgents();
  const createAgent = useCreateAgent();
  const agentMutations = useAgentMutations();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [modelProvider, setModelProvider] = useState("workers_ai");
  const [modelName, setModelName] = useState("@cf/meta/llama-3.3-70b-instruct");
  const [shared, setShared] = useState(false);
  const error = useErrorText(agents.error ?? createAgent.error, "could not load agents");

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Agents</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        Custom agents define their own system prompts, model providers, and scopes.
      </p>

      <Card title="Create an agent">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Code Reviewer" />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Specialized in reviewing code and PRs" />
          <TextArea label="System prompt" rows={3} value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} placeholder="You are an expert code reviewer..." />
          <Select
            label="Model provider"
            options={[
              { value: "workers_ai", label: "Cloudflare Workers AI (managed default)" },
              { value: "anthropic", label: "Anthropic (BYOK)" },
              { value: "openai_compatible", label: "OpenAI-compatible endpoint (BYOK)" },
            ]}
            value={modelProvider}
            onChange={(e) => setModelProvider(e.target.value)}
          />
          <TextField label="Model identifier" value={modelName} onChange={(e) => setModelName(e.target.value)} />
          <Switch label="Shared with workspace" checked={shared} onChange={setShared} />
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Button
              variant="primary"
              loading={createAgent.isPending}
              disabled={name.trim().length === 0}
              onClick={() => {
                void createAgent
                  .mutateAsync({
                    name: name.trim(),
                    description: description.trim() || undefined,
                    systemPrompt: systemPrompt.trim() || undefined,
                    modelProvider,
                    modelName: modelName.trim() || undefined,
                    shared,
                  })
                  .then(() => {
                    setName("");
                    setDescription("");
                    setSystemPrompt("");
                  });
              }}
            >
              Create agent
            </Button>
          </div>
          {error ? <span style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-sm)" }}>{error}</span> : null}
        </div>
      </Card>

      {agents.isPending ? (
        <Spinner label="Loading agents" />
      ) : (agents.data?.agents ?? []).length === 0 ? (
        <EmptyState title="No agents created yet" description="Create an agent to give it a specific persona and model." />
      ) : (
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          {(agents.data?.agents ?? []).map((agent) => (
            <Card key={agent.agentId} title={agent.name} subtitle={`${agent.modelProvider ?? "default provider"} · ${agent.shared ? "workspace" : "personal"}`}>
              <div style={stackStyle("var(--dayvise-space-xs)")}>
                {agent.description ? <p style={{ margin: 0, fontSize: "var(--dayvise-font-size-sm)" }}>{agent.description}</p> : null}
                <p style={{ margin: 0 }}>Personality: {agent.personality ?? "none"}. Select one in a chat with /personality.</p>
                {agent.systemPrompt ? (
                  <pre
                    style={{
                      margin: 0,
                      padding: "var(--dayvise-space-xs)",
                      background: "var(--dayvise-color-raised)",
                      borderRadius: "var(--dayvise-radius-sm)",
                      fontSize: "var(--dayvise-font-size-xs)",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {agent.systemPrompt}
                  </pre>
                ) : null}
                <div style={rowStyle("var(--dayvise-space-xs)")}>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void navigate({ to: "/", search: { agentId: agent.agentId } as never })}
                  >
                    Start conversation
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={agentMutations.share.isPending || agentMutations.unshare.isPending}
                    onClick={() =>
                      void (agent.shared
                        ? agentMutations.unshare.mutateAsync(agent.agentId)
                        : agentMutations.share.mutateAsync(agent.agentId))
                    }
                  >
                    {agent.shared ? "Make personal" : "Share"}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={agentMutations.archive.isPending}
                    onClick={() => void agentMutations.archive.mutateAsync(agent.agentId)}
                  >
                    Archive
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------- schedules ------------------------------- */

export function SchedulesPage() {
  const schedules = useSchedules();
  const agents = useAgents();
  const mutations = useScheduleMutations();
  const error = useErrorText(schedules.error ?? mutations.create.error, "could not load schedules");

  const agentOptions = (agents.data?.agents ?? []).map((agent) => ({ value: agent.agentId, label: agent.name }));
  const [agentId, setAgentId] = useState("");
  const selectedAgent = agentId || agentOptions[0]?.value || "";
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [cron, setCron] = useState("0 8 * * *");
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const [overlap, setOverlap] = useState<"queue" | "skip" | "parallel">("queue");
  const [missedPolicy, setMissedPolicy] = useState<"skip" | "run_once" | "run_all">("skip");
  const [notifyOnCompletion, setNotifyOnCompletion] = useState(true);

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Schedules</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>Schedules run in the cloud, even when every client is closed.</p>

      <Card title="New schedule">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <Select
            label="Agent"
            options={agentOptions.length > 0 ? agentOptions : [{ value: "", label: "Create an agent first" }]}
            value={selectedAgent}
            disabled={agentOptions.length === 0}
            onChange={(event) => setAgentId(event.target.value)}
          />
          <TextField label="Title" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Morning release digest" />
          <TextArea label="Prompt" rows={3} value={prompt} onChange={(event) => setPrompt(event.target.value)} />
          <TextField label="Cron expression" hint="Five fields: minute hour day-of-month month day-of-week" value={cron} onChange={(event) => setCron(event.target.value)} />
          <TextField label="Timezone" hint="Evaluated in this zone, including across daylight-saving changes." value={timezone} onChange={(event) => setTimezone(event.target.value)} />
          <Select
            label="If a previous run is still going"
            options={[
              { value: "queue", label: "Queue the next run (safe default)" },
              { value: "skip", label: "Skip this occurrence" },
              { value: "parallel", label: "Run in parallel" },
            ]}
            value={overlap}
            onChange={(event) => setOverlap(event.target.value as "queue" | "skip" | "parallel")}
          />
          <Select
            label="If a run was missed"
            options={[
              { value: "skip", label: "Skip missed runs" },
              { value: "run_once", label: "Run once on recovery" },
              { value: "run_all", label: "Run all missed occurrences" },
            ]}
            value={missedPolicy}
            onChange={(event) => setMissedPolicy(event.target.value as "skip" | "run_once" | "run_all")}
          />
          <Switch label="Notify on completion" checked={notifyOnCompletion} onChange={setNotifyOnCompletion} />
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Button
              variant="primary"
              loading={mutations.create.isPending}
              disabled={selectedAgent.length === 0 || title.trim().length === 0 || prompt.trim().length === 0}
              onClick={() =>
                void mutations.create
                  .mutateAsync({
                    agentId: selectedAgent,
                    title,
                    prompt,
                    cron,
                    timezone,
                    overlapPolicy: overlap,
                    missedRunPolicy: missedPolicy,
                    notifyOnCompletion,
                  })
                  .then(() => {
                    setTitle("");
                    setPrompt("");
                  })
              }
            >
              Create schedule
            </Button>
          </div>
          {error ? <span style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-sm)" }}>{error}</span> : null}
        </div>
      </Card>

      <ScheduleList
        schedules={schedules.data?.schedules ?? []}
        busy={mutations.enable.isPending || mutations.disable.isPending || mutations.trigger.isPending || mutations.remove.isPending}
        onToggle={(schedule) => (schedule.enabled ? mutations.disable.mutateAsync(schedule.scheduleId) : mutations.enable.mutateAsync(schedule.scheduleId)).then(() => undefined)}
        onTrigger={(schedule) => mutations.trigger.mutateAsync(schedule.scheduleId).then(() => undefined)}
        onDelete={(schedule) => mutations.remove.mutateAsync(schedule.scheduleId).then(() => undefined)}
      />
    </div>
  );
}

/* --------------------------------- memory --------------------------------- */

export function MemoryPage() {
  const memory = useMemory();
  const mutations = useMemoryMutations();
  const [content, setContent] = useState("");
  const error = useErrorText(memory.error ?? mutations.create.error ?? mutations.confirm.error ?? mutations.promote.error ?? mutations.remove.error, "could not update memory");

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Memory</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        The agent learns from your runs. Nothing becomes visible to your workspace until you share it, and no administrator can read
        what you have not shared.
      </p>

      <Card title="Add a memory">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <TextArea label="What should the agent remember?" rows={2} value={content} onChange={(event) => setContent(event.target.value)} />
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Button
              variant="primary"
              loading={mutations.create.isPending}
              disabled={content.trim().length === 0}
              onClick={() => void mutations.create.mutateAsync({ content, scope: "private" }).then(() => setContent(""))}
            >
              Save privately
            </Button>
          </div>
          {error ? <span style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-sm)" }}>{error}</span> : null}
        </div>
      </Card>

      <MemoryList
        privateMemories={memory.data?.private ?? []}
        sharedMemories={memory.data?.shared ?? []}
        busy={mutations.confirm.isPending || mutations.promote.isPending || mutations.remove.isPending}
        onConfirm={(item) => mutations.confirm.mutateAsync({ memoryId: item.memoryId, version: item.version, content: item.content }).then(() => undefined)}
        onPromote={(item) => mutations.promote.mutateAsync(item.memoryId).then(() => undefined)}
        onDelete={(item) => mutations.remove.mutateAsync(item.memoryId).then(() => undefined)}
      />
    </div>
  );
}

/* ---------------------------------- files --------------------------------- */

export function FilesPage() {
  const download = useFileDownload();
  const files = useFiles();
  const error = useErrorText(files.error, "could not load files");
  const items = (files.data?.files ?? []).map((file) => ({
    fileId: file.fileId,
    path: file.path,
    version: file.version,
    mimeType: file.mimeType,
    size: file.size,
    storageProvider: file.storageProvider,
    updatedAt: file.updatedAt,
  }));

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Files</h1>
      {download.error ? <span role="alert">{download.error}</span> : null}
      {error ? (
        // An error is not an empty list. Rendering the list here showed "No files yet" over
        // a request that never completed, which reads as a successful empty result -- the
        // one thing an empty state must never mean.
        <span style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-sm)" }}>{error}</span>
      ) : files.isPending ? (
        <Spinner label="Loading files" />
      ) : (
        <FileArtifactList files={items} openLabel="Download" onOpen={(file) => void download.download(file.fileId)} />
      )}
    </div>
  );
}

/* --------------------------------- usage ---------------------------------- */

export function UsagePage() {
  const usage = useUsage();
  const entitlements = useEntitlements();

  const meters = new Map((usage.data?.meters ?? []).map((entry) => [entry.meter, entry.total]));
  const storage = usage.data?.storage;
  const storageNotice = storage === undefined ? null
    : !storage.reconciled ? "Storage accounting is being initialized; retained usage may not be final yet."
    : storage.reservedCount > 0 ? `${storage.reservedCount} in-flight write(s) reserving ${storage.reservedBytes} bytes.`
    : storage.retainedCount === 0 ? "No retained files, artifacts or runtime blobs."
    : `${storage.retainedCount} retained object(s).`;

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Usage</h1>
      {usage.isPending || entitlements.isPending ? (
        <Spinner label="Loading usage" />
      ) : (
        <UsagePanel
          planLabel={entitlements.data?.plan ?? "unknown"}
          byokEnabled={entitlementAllowed(entitlements.data?.byok)}
          aiTokens={{ used: meters.get("workers_ai_tokens") ?? 0, limit: entitlements.data?.monthlyManagedAiTokens ?? null }}
          byokTokens={{ used: meters.get("byok_tokens") ?? 0, limit: null }}
          containerMs={{ used: meters.get("container_ms") ?? 0, limit: entitlements.data?.monthlyContainerMs ?? null }}
          storageBytes={{ used: storage?.retainedBytes ?? 0, limit: storage?.limitBytes ?? entitlements.data?.storageBytes ?? null }}
          storageNotice={storageNotice}
        />
      )}
    </div>
  );
}

/* --------------------------------- billing -------------------------------- */

/** A published price is dollars already; the page formats, it does not convert. */


/**
 * Billing, read from the control plane rather than inferred from a plan name.
 *
 * Every state this page can be in is rendered as itself. A load failure says so instead of drawing
 * an empty plan list, which would claim "this workspace has no plans" — a different statement and a
 * false one. When the deployment has no payment provider, buying is disabled and the reason is
 * stated, because a button that silently does nothing teaches the person clicking it nothing.
 *
 * Starting a checkout is not treated as a plan change: the subscription is active when the
 * provider's webhook says so, and this page will not imply otherwise.
 */
export function BillingPage() { return <BillingPanel />; }

/**
 * Configuration snapshots plus the memory provenance view. Snapshots cover
 * private agents and skills only; the provenance view shows recorded edges,
 * never inferred ones.
 */
export function SnapshotsPage() {
  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Snapshots & provenance</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        Back up or restore your private agent configuration and skills, and inspect where memories and learning came from.
      </p>
      <SnapshotsPanel />
      <MemoryGraphPanel />
    </div>
  );
}

export function ProactivePage() {
  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Call me when</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        Presence-aware notification and call preferences for this account. Missing permissions or credentials surface here too.
      </p>
      <CallPreferencesPanel />
    </div>
  );
}

export function RuntimePage() {
  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Codex runtime</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        ChatGPT-plan connection status and the optional Codex runtime adapter state. BYO API keys stay a separate explicit choice.
      </p>
      <CodexRuntimeCard />
    </div>
  );
}

export function YoloPage() {
  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Elevated autonomy</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        A recorded preference, not a grant: permissions, allowlists, approvals and quotas still decide what a run may do.
      </p>
      <YoloModePanel />
    </div>
  );
}

/* ------------------------------- providers -------------------------------- */

export function ProvidersPage() {
  const credentials = useCredentials();
  const mutations = useCredentialMutations();
  const [provider, setProvider] = useState("anthropic");
  const [label, setLabel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const error = useErrorText(mutations.create.error, "the credential could not be saved");

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Providers</h1>
      <DefaultWorkspaceCard />
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        Bring your own model provider, or use the managed default. Keys are encrypted before they are stored and are never shown
        again after saving.
      </p>

      <Card title="Add a provider key">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <Select
            label="Provider"
            options={[
              { value: "anthropic", label: "Anthropic" },
              { value: "openai_compatible", label: "OpenAI-compatible endpoint" },
            ]}
            value={provider}
            onChange={(event) => setProvider(event.target.value)}
          />
          <TextField label="Label" value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Work Anthropic key" />
          <TextField label="API key" secret value={apiKey} onChange={(event) => setApiKey(event.target.value)} />
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Button
              variant="primary"
              loading={mutations.create.isPending}
              disabled={label.trim().length === 0 || apiKey.trim().length === 0}
              onClick={() =>
                void mutations.create
                  .mutateAsync({ kind: "ai_provider", provider, label, secret: { apiKey } })
                  .then(() => {
                    setApiKey("");
                    setLabel("");
                  })
              }
            >
              Save key
            </Button>
          </div>
          {error ? <span style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-sm)" }}>{error}</span> : null}
        </div>
      </Card>

      {(credentials.data?.aiProviders ?? []).length === 0 ? (
        <EmptyState title="No provider keys yet" description="Add one to route model calls to your own account." />
      ) : (
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          {(credentials.data?.aiProviders ?? []).map((credential) => (
            <Card key={credential.credentialId} title={credential.label} subtitle={`${credential.provider} · ${credential.scope}`}>
              <div style={rowStyle("var(--dayvise-space-xs)")}>
                <Button size="sm" variant="secondary" loading={mutations.test.isPending} onClick={() => void mutations.test.mutateAsync(credential.credentialId)}>
                  Test connection
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void mutations.revoke.mutateAsync(credential.credentialId)}>
                  Revoke
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card
        title="Saved reusable secrets"
        subtitle="Secrets you chose to save from a secure text box. They stay available for later tasks until you revoke them."
      >
        {(credentials.data?.secrets ?? []).length === 0 ? (
          <p style={{ ...MUTED_TEXT, margin: 0 }}>
            Nothing saved yet. Type a secret in a message and choose Save for reuse to keep it here.
          </p>
        ) : (
          <div style={stackStyle("var(--dayvise-space-sm)")}>
            {(credentials.data?.secrets ?? []).map((credential) => (
              <div key={credential.credentialId} style={rowStyle("var(--dayvise-space-sm)")}>
                <span>{credential.label}</span>
                <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-sm)" }}>{`${credential.scope} · saved ${credential.createdAt}`}</span>
                <Button size="sm" variant="ghost" loading={mutations.revoke.isPending} onClick={() => void mutations.revoke.mutateAsync(credential.credentialId)}>
                  Revoke
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* -------------------------------- learning -------------------------------- */

function refinementTone(state: string): "neutral" | "accent" | "success" | "danger" {
  if (state === "candidate") return "accent";
  if (state === "applied") return "success";
  if (state === "failed" || state === "quarantined") return "danger";
  return "neutral";
}

export function BoardsPage() {
  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Boards</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        Durable boards, their tasks and the agent runs working on them. A task runs on its own branch; only the board owner marks it done.
      </p>
      <BoardsSection />
    </div>
  );
}

export function LearningPage() {
  const refinements = useRefinements();
  const mutations = useRefinementMutations();
  const applyError = useErrorText(mutations.apply.error, "the candidate could not be applied");
  const revertError = useErrorText(mutations.revert.error, "the refinement could not be reverted");
  const rows = refinements.data?.refinements ?? [];

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Learning</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        Refinements derived from conversations you recorded. A candidate changes nothing until you apply it, applying affects
        subsequent runs only, and an applied refinement can be reverted while nothing newer edits the same target. Create one from
        the conversation that holds the material with /refine memory &lt;memoryId&gt; or /refine skill &lt;skillId&gt;.
      </p>
      {applyError ? (
        <span role="alert" style={{ color: "var(--dayvise-color-danger)" }}>
          {applyError}
        </span>
      ) : null}
      {revertError ? (
        <span role="alert" style={{ color: "var(--dayvise-color-danger)" }}>
          {revertError}
        </span>
      ) : null}
      {refinements.isPending ? (
        <Spinner label="Loading refinements" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No refinements yet"
          description="Run /refine in the conversation where the material was recorded to derive a candidate revision of a memory or skill."
        />
      ) : (
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          {rows.map((refinement) => (
            <Card
              key={refinement.refinementId}
              title={`${refinement.targetKind} ${refinement.targetId ?? ""}`}
              subtitle={refinement.reason}
              footer={
                <div style={rowStyle("var(--dayvise-space-xs)")}>
                  {refinement.state === "candidate" ? (
                    <Button
                      size="sm"
                      variant="primary"
                      loading={mutations.apply.isPending}
                      onClick={() => void mutations.apply.mutateAsync(refinement.refinementId)}
                    >
                      Apply candidate
                    </Button>
                  ) : null}
                  {refinement.state === "applied" ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={mutations.revert.isPending}
                      onClick={() => void mutations.revert.mutateAsync(refinement.refinementId)}
                    >
                      Revert
                    </Button>
                  ) : null}
                  <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{refinement.refinementId}</span>
                </div>
              }
            >
              <div style={stackStyle("var(--dayvise-space-xs)")}>
                <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
                  <Badge tone={refinementTone(refinement.state)}>{refinement.state}</Badge>
                  <Badge tone="neutral">{`${refinement.sourceMessageIds.length} source message(s)`}</Badge>
                  <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{`updated ${refinement.updatedAt}`}</span>
                </div>
                <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
                  {`Base revision ${refinement.baseRevision ?? "n/a"}${refinement.resultRevision === null ? "" : ` to ${refinement.resultRevision}`} · source conversation ${refinement.sourceConversationId ?? "none"}`}
                </span>
                {refinement.error ? (
                  <span role="alert" style={{ color: "var(--dayvise-color-danger)" }}>
                    {refinement.error}
                  </span>
                ) : null}
                <details>
                  <summary style={{ cursor: "pointer", ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-sm)" }}>Candidate revision</summary>
                  <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontSize: "var(--dayvise-font-size-sm)", maxHeight: "240px", overflow: "auto" }}>
                    {refinement.proposal.slice(0, 4000)}
                  </pre>
                </details>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* --------------------------------- storage -------------------------------- */

export function StoragePage() {
  const { session, switchWorkspace } = useSession();
  const credentials = useCredentials();
  const mutations = useCredentialMutations();
  const storageRoute = useStorageRoute();
  const storageMutations = useStorageMutations();

  const [migrationDirection, setMigrationDirection] = useState<"d1_to_postgres" | "postgres_to_d1" | "r2_to_s3" | "s3_to_r2">("d1_to_postgres");
  const [migrationTargetCredentialId, setMigrationTargetCredentialId] = useState("");
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const migrationJob = useStorageMigrationJob(activeJobId);

  // PostgreSQL form state
  const [pgLabel, setPgLabel] = useState("");
  const [pgHost, setPgHost] = useState("");
  const [pgPort, setPgPort] = useState("5432");
  const [pgDb, setPgDb] = useState("");
  const [pgUser, setPgUser] = useState("");
  const [pgPassword, setPgPassword] = useState("");
  const [pgSsl, setPgSsl] = useState("require");

  // S3 form state
  const [s3Label, setS3Label] = useState("");
  const [s3Bucket, setS3Bucket] = useState("");
  const [s3Region, setS3Region] = useState("auto");
  const [s3Endpoint, setS3Endpoint] = useState("");
  const [s3AccessKey, setS3AccessKey] = useState("");
  const [s3SecretKey, setS3SecretKey] = useState("");

  const error = useErrorText(mutations.create.error, "could not save storage credential");
  const migrationError = useErrorText(storageMutations.startMigration.error, "could not start migration");
  const migrationNeedsByoTarget = migrationDirection === "d1_to_postgres" || migrationDirection === "r2_to_s3";
  const migrationTargetOptions = migrationDirection === "d1_to_postgres"
    ? (credentials.data?.databases ?? []).map((credential) => ({ value: credential.credentialId, label: `${credential.label} · PostgreSQL` }))
    : migrationDirection === "r2_to_s3"
      ? (credentials.data?.blobs ?? []).map((credential) => ({ value: credential.credentialId, label: `${credential.label} · S3` }))
      : [];

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Storage</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        Choose where your workspace's data lives. Managed D1 and R2 are the default; a PostgreSQL database and S3-compatible bucket
        of your own are available on every plan.
      </p>
      <StorageRouteCard
        route={
          storageRoute.data ?? {
            structured: "d1_shared",
            blob: "r2_managed",
            health: "healthy",
            lastCheckedAt: null,
          }
        }
      />

      <Card title="Data Migration" subtitle="Move data between managed and BYO storage (prompt.md §56)">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <p style={{ margin: 0, fontSize: "var(--dayvise-font-size-sm)" }}>
            Migrate tables or blobs with SHA-256 checksum verification. Writes pause while the workspace is copied. The source remains active if the copy fails; a verified copy activates the destination automatically.
          </p>
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Select
              label="Direction"
              value={migrationDirection}
              onChange={(e) => {
                  setMigrationDirection(e.target.value as never);
                  setMigrationTargetCredentialId("");
                }}
              options={[
                { value: "d1_to_postgres", label: "D1 → PostgreSQL" },
                { value: "postgres_to_d1", label: "PostgreSQL → D1" },
                { value: "r2_to_s3", label: "R2 → BYO S3" },
                { value: "s3_to_r2", label: "BYO S3 → R2" },
              ]}
            />
            {migrationNeedsByoTarget ? (
              <Select
                label="Destination credential"
                value={migrationTargetCredentialId}
                onChange={(e) => setMigrationTargetCredentialId(e.target.value)}
                options={[{ value: "", label: "Choose a connected destination" }, ...migrationTargetOptions]}
              />
            ) : null}
            <Button
              variant="primary"
              loading={storageMutations.startMigration.isPending}
              disabled={migrationNeedsByoTarget && !migrationTargetOptions.some((option) => option.value === migrationTargetCredentialId)}
              onClick={() => {
                void storageMutations.startMigration
                  .mutateAsync({
                    direction: migrationDirection,
                    ...(migrationNeedsByoTarget ? { targetCredentialId: migrationTargetCredentialId } : {}),
                  })
                  .then((res) => {
                    setActiveJobId(res.job.jobId);
                  });
              }}
            >
              Start migration
            </Button>
          </div>
          {migrationError ? (
            <span style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-sm)" }}>
              {migrationError}
            </span>
          ) : null}
          {activeJobId && migrationJob.data?.job ? (
            <div style={stackStyle("var(--dayvise-space-xs)")}>
              <span style={{ fontSize: "var(--dayvise-font-size-sm)", fontWeight: "var(--dayvise-font-weight-semibold)" }}>
                Job {migrationJob.data.job.jobId} ({migrationJob.data.job.status}): Step {migrationJob.data.job.progress.stepsCompleted}/{migrationJob.data.job.progress.totalSteps} — {migrationJob.data.job.progress.currentStep}
              </span>
              {migrationJob.data.job.error ? (
                <p style={{ color: "var(--dayvise-color-danger)", margin: 0 }}>{migrationJob.data.job.error}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      <Card title="Bring Your Own PostgreSQL Database" subtitle="Store structured data in your own hosted database">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <TextField label="Label" value={pgLabel} onChange={(e) => setPgLabel(e.target.value)} placeholder="Production DB" />
          <TextField label="Host" value={pgHost} onChange={(e) => setPgHost(e.target.value)} placeholder="postgres.internal.example.com" />
          <TextField label="Port" value={pgPort} onChange={(e) => setPgPort(e.target.value)} />
          <TextField label="Database name" value={pgDb} onChange={(e) => setPgDb(e.target.value)} placeholder="dayvise" />
          <TextField label="User" value={pgUser} onChange={(e) => setPgUser(e.target.value)} placeholder="dayvise_app" />
          <TextField label="Password" secret value={pgPassword} onChange={(e) => setPgPassword(e.target.value)} />
          <Select
            label="SSL mode"
            options={[
              { value: "require", label: "Require (recommended)" },
              { value: "prefer", label: "Prefer" },
              { value: "disable", label: "Disable" },
            ]}
            value={pgSsl}
            onChange={(e) => setPgSsl(e.target.value)}
          />
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Button
              variant="primary"
              loading={mutations.create.isPending}
              disabled={pgLabel.trim().length === 0 || pgHost.trim().length === 0 || pgDb.trim().length === 0 || pgUser.trim().length === 0}
              onClick={() => {
                void mutations.create.mutateAsync({
                  kind: "byo_database",
                  provider: "postgres",
                  label: pgLabel.trim(),
                  secret: { host: pgHost.trim(), port: pgPort.trim(), database: pgDb.trim(), user: pgUser.trim(), password: pgPassword, ssl: pgSsl },
                }).then(() => {
                  setPgLabel("");
                  setPgHost("");
                  setPgDb("");
                  setPgUser("");
                  setPgPassword("");
                });
              }}
            >
              Connect PostgreSQL database
            </Button>
          </div>
          {(credentials.data?.databases ?? []).length > 0 ? (
            <div style={stackStyle("var(--dayvise-space-xs)")}>
              <span style={{ fontSize: "var(--dayvise-font-size-sm)", fontWeight: "var(--dayvise-font-weight-semibold)" }}>Configured databases:</span>
              {(credentials.data?.databases ?? []).map((db) => (
                <Card key={db.credentialId} title={db.label} subtitle={`${db.provider} · ${db.scope}`}>
                  <div style={rowStyle("var(--dayvise-space-xs)")}>
                    <Button size="sm" variant="secondary" loading={mutations.test.isPending} onClick={() => void mutations.test.mutateAsync(db.credentialId)}>
                      Test connection
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => void mutations.revoke.mutateAsync(db.credentialId)}>
                      Revoke
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : null}
        </div>
      </Card>

      <Card title="Bring Your Own Object Storage (S3 / R2)" subtitle="Store file artifacts and large attachments in your own S3 bucket">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <TextField label="Label" value={s3Label} onChange={(e) => setS3Label(e.target.value)} placeholder="Workspace S3 Bucket" />
          <TextField label="Bucket name" value={s3Bucket} onChange={(e) => setS3Bucket(e.target.value)} placeholder="my-agent-blobs" />
          <TextField label="Region" value={s3Region} onChange={(e) => setS3Region(e.target.value)} placeholder="us-east-1" />
          <TextField label="Endpoint" value={s3Endpoint} onChange={(e) => setS3Endpoint(e.target.value)} placeholder="https://<account>.r2.cloudflarestorage.com (optional)" />
          <TextField label="Access key ID" value={s3AccessKey} onChange={(e) => setS3AccessKey(e.target.value)} />
          <TextField label="Secret access key" secret value={s3SecretKey} onChange={(e) => setS3SecretKey(e.target.value)} />
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Button
              variant="primary"
              loading={mutations.create.isPending}
              disabled={s3Label.trim().length === 0 || s3Bucket.trim().length === 0 || s3AccessKey.trim().length === 0}
              onClick={() => {
                void mutations.create.mutateAsync({
                  kind: "byo_blob",
                  provider: "s3",
                  label: s3Label.trim(),
                  secret: { bucket: s3Bucket.trim(), region: s3Region.trim(), endpoint: s3Endpoint.trim(), accessKeyId: s3AccessKey.trim(), secretAccessKey: s3SecretKey },
                }).then(() => {
                  setS3Label("");
                  setS3Bucket("");
                  setS3Endpoint("");
                  setS3AccessKey("");
                  setS3SecretKey("");
                });
              }}
            >
              Connect S3 storage
            </Button>
          </div>
          {(credentials.data?.blobs ?? []).length > 0 ? (
            <div style={stackStyle("var(--dayvise-space-xs)")}>
              <span style={{ fontSize: "var(--dayvise-font-size-sm)", fontWeight: "var(--dayvise-font-weight-semibold)" }}>Configured object stores:</span>
              {(credentials.data?.blobs ?? []).map((blob) => (
                <Card key={blob.credentialId} title={blob.label} subtitle={`${blob.provider} · ${blob.scope}`}>
                  <div style={rowStyle("var(--dayvise-space-xs)")}>
                    <Button size="sm" variant="secondary" loading={mutations.test.isPending} onClick={() => void mutations.test.mutateAsync(blob.credentialId)}>
                      Test connection
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => void mutations.revoke.mutateAsync(blob.credentialId)}>
                      Revoke
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : null}
          {error ? <span style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-sm)" }}>{error}</span> : null}
        </div>
      </Card>

      <Card title="Workspaces" subtitle="Switching loads the storage route configured for that workspace.">
        <WorkspaceSwitcher
          workspaces={(session.workspaces ?? []).map((workspace) => ({
            workspaceId: workspace.workspaceId,
            name: workspace.name,
            kind: workspace.kind,
            plan: workspace.plan,
            planStatus: workspace.planStatus,
            role: workspace.role,
          }))}
          activeWorkspaceId={session.workspace?.workspaceId ?? null}
          onSelect={(workspaceId) => switchWorkspace(workspaceId as never)}
        />
      </Card>
    </div>
  );
}

/* ---------------------------------- tools --------------------------------- */

/**
 * Tool permissions, MCP servers, and Learned Skills.
 */
export function ToolsPage() {
  const { session } = useSession();

  // MCP Servers
  const mcpServers = useMcpServers();
  const mcpMutations = useMcpServerMutations();
  const [mcpName, setMcpName] = useState("");
  const [mcpUrl, setMcpUrl] = useState("");
  const [mcpTransport, setMcpTransport] = useState<"http" | "stdio">("http");
  const [mcpAuthType, setMcpAuthType] = useState<"none" | "bearer">("none");
  const [mcpCredentialValue, setMcpCredentialValue] = useState("");
  const [mcpShared, setMcpShared] = useState(false);

  // Skills
  const skills = useSkills();
  const skillMutations = useSkillMutations();
  const [skillName, setSkillName] = useState("");
  const [skillInstructions, setSkillInstructions] = useState("");
  const [skillVisibility, setSkillVisibility] = useState<"private" | "workspace">("private");

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Tools & Capabilities</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        Every tool call is authorized on the server before it runs. Connect external MCP servers and define reusable skills.
      </p>

      <ToolPermissionsPanel />
      <McpServersPanel />
      <SkillsPanel />
      <SkillBundlesPanel />
      <AutomationTemplatesPanel />
      <CallPreferencesPanel />
      <YoloModePanel />

      {/* MCP Servers */}
      <Card title="Model Context Protocol (MCP) Servers" subtitle="Connect external tool servers speaking JSON-RPC 2.0">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <TextField label="Server name" value={mcpName} onChange={(e) => setMcpName(e.target.value)} placeholder="filesystem" />
          <TextField label="Server URL" value={mcpUrl} onChange={(e) => setMcpUrl(e.target.value)} placeholder="https://mcp.example.com/v1" disabled={mcpTransport === "stdio"} />
          <Select
            label="Transport"
            options={[
              { value: "http", label: "HTTP (Standard POST)" },
              { value: "stdio", label: "Standard I/O (local container)" },
            ]}
            value={mcpTransport}
            onChange={(e) => {
              const transport = e.target.value as "http" | "stdio";
              setMcpTransport(transport);
              if (transport === "stdio") {
                setMcpAuthType("none");
                setMcpCredentialValue("");
              }
            }}
          />
          <Select
            label="Authentication"
            options={[
              { value: "none", label: "None" },
              { value: "bearer", label: "Bearer token" },
            ]}
            value={mcpAuthType}
            disabled={mcpTransport === "stdio"}
            onChange={(e) => setMcpAuthType(e.target.value as "none" | "bearer")}
          />
          {mcpTransport === "http" && mcpAuthType === "bearer" ? (
            <TextField
              label="Bearer token"
              value={mcpCredentialValue}
              onChange={(e) => setMcpCredentialValue(e.target.value)}
              secret
              autoComplete="new-password"
              hint="Stored encrypted and sent only to this HTTPS MCP endpoint."
            />
          ) : null}
          <Switch
            label="Shared with workspace"
            checked={mcpShared}
            disabled={session.workspace?.role !== "owner" && session.workspace?.role !== "admin"}
            onChange={setMcpShared}
          />
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Button
              variant="primary"
              loading={mcpMutations.create.isPending}
              disabled={mcpName.trim().length === 0}
              onClick={() => {
                void mcpMutations.create
                  .mutateAsync({
                    name: mcpName.trim(),
                    url: mcpTransport === "http" ? mcpUrl.trim() || null : null,
                    transport: mcpTransport,
                    authType: mcpAuthType,
                    credentialValue: mcpTransport === "http" && mcpAuthType === "bearer" ? mcpCredentialValue : undefined,
                    shared: mcpShared,
                  })
                  .then(() => {
                    setMcpName("");
                    setMcpUrl("");
                    setMcpCredentialValue("");
                  });
              }}
            >
              Connect MCP server
            </Button>
          </div>

          {(mcpServers.data?.servers ?? []).length === 0 ? (
            <EmptyState title="No MCP servers connected" description="Connect an MCP server to add custom tools to your runs." />
          ) : (
            <div style={stackStyle("var(--dayvise-space-xs)")}>
              {(mcpServers.data?.servers ?? []).map((srv) => (
                <Card key={srv.mcpServerId} title={srv.name} subtitle={`${srv.transport.toUpperCase()} · ${srv.shared ? "workspace" : "personal"} · ${srv.enabled ? "enabled" : "disabled"}`}>
                  <div style={stackStyle("var(--dayvise-space-xs)")}>
                    {srv.url ? <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{srv.url}</span> : null}
                    {(srv.tools ?? []).length > 0 ? (
                      <span style={{ fontSize: "var(--dayvise-font-size-xs)" }}>
                        Discovered tools: {srv.tools?.map((t) => t.name).join(", ")}
                      </span>
                    ) : null}
                    <div style={rowStyle("var(--dayvise-space-xs)")}>
                      <Button size="sm" variant="secondary" loading={mcpMutations.test.isPending} onClick={() => void mcpMutations.test.mutateAsync(srv.mcpServerId)}>
                        Test & Discover tools
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => void mcpMutations.remove.mutateAsync(srv.mcpServerId)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Learned Skills */}
      <Card title="Agent Skills" subtitle="Reusable skills with YAML frontmatter following the Dayvise SKILL.md contract">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <TextField label="Skill name" value={skillName} onChange={(e) => setSkillName(e.target.value)} placeholder="git-release" />
          <TextArea
            label="Instructions (Markdown with YAML frontmatter)"
            rows={4}
            value={skillInstructions}
            onChange={(e) => setSkillInstructions(e.target.value)}
            placeholder={"---\nname: git-release\ndescription: Create a release branch and draft changelog\n---\nWhen asked to release..."}
          />
          <Select
            label="Visibility"
            options={[
              { value: "workspace", label: "Shared with workspace" },
              { value: "private", label: "Private to me" },
            ]}
            value={skillVisibility}
            onChange={(e) => setSkillVisibility(e.target.value as "private" | "workspace")}
          />
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Button
              variant="primary"
              loading={skillMutations.create.isPending}
              disabled={skillName.trim().length === 0 || skillInstructions.trim().length === 0}
              onClick={() => {
                void skillMutations.create
                  .mutateAsync({
                    name: skillName.trim(),
                    instructions: skillInstructions.trim(),
                    visibility: skillVisibility,
                  })
                  .then(() => {
                    setSkillName("");
                    setSkillInstructions("");
                    setSkillVisibility("private");
                  });
              }}
            >
              Save skill
            </Button>
          </div>

          {(skills.data?.skills ?? []).length === 0 ? (
            <EmptyState title="No skills defined yet" description="Teach agents new skills using standard SKILL.md documents." />
          ) : (
            <div style={stackStyle("var(--dayvise-space-xs)")}>
              {(skills.data?.skills ?? []).map((skill) => {
                const desc = typeof skill.frontmatter?.description === "string" ? skill.frontmatter.description : null;
                return (
                  <Card key={skill.skillId} title={skill.name} subtitle={`${skill.visibility} · version ${skill.version}`}>
                    <div style={stackStyle("var(--dayvise-space-xs)")}>
                      {desc ? <p style={{ margin: 0, fontSize: "var(--dayvise-font-size-sm)" }}>{desc}</p> : null}
                      <div style={rowStyle("var(--dayvise-space-xs)")}>
                        <SkillExportButton skillId={skill.skillId} />
                        <Button size="sm" variant="ghost" onClick={() => void skillMutations.remove.mutateAsync(skill.skillId)}>
                          Remove
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

/* ------------------------------ notifications ----------------------------- */

export function NotificationsPage() {
  const notifications = useNotifications();
  const navigate = useNavigate();

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Notifications</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        In-app notifications for approval requests, run completions, and system events. Questions a run is waiting on
        appear here until they are answered, including ones asked while this client was closed.
      </p>

      <InputRequestInboxSection
        onOpenConversation={(conversationId) => {
          void navigate({ to: "/c/$conversationId", params: { conversationId } });
        }}
      />

      <WebPushSettingsCard />

      {notifications.isPending ? (
        <Spinner label="Loading notifications" />
      ) : (notifications.data?.notifications ?? []).length === 0 ? (
        <EmptyState title="No notifications yet" description="You are all caught up! Run events and approval requests will appear here." />
      ) : (
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          {(notifications.data?.notifications ?? []).map((item) => (
            <Card key={item.notificationId} title={item.title} subtitle={`${item.kind} · ${new Date(item.createdAt).toLocaleString()}`}>
              <div style={stackStyle("var(--dayvise-space-xs)")}>
                {item.body ? <p style={{ margin: 0, fontSize: "var(--dayvise-font-size-sm)" }}>{item.body}</p> : null}
                {item.runId ? (
                  <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
                    Run: {item.runId}
                  </span>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------- admin --------------------------------- */

export function AdminPage() {
  const { session, switchAccount, signOut } = useSession();
  const entitlements = useEntitlements();
  const isAdmin = session.workspace?.role === "owner" || session.workspace?.role === "admin";
  const audit = useAuditLog(isAdmin);
  const members = useAdminMembers(isAdmin);
  const sharedMemories = useAdminSharedMemories(isAdmin);
  const adminMutations = useAdminMutations();

  const [newUserId, setNewUserId] = useState("");
  const [newRole, setNewRole] = useState<"admin" | "member" | "viewer">("member");
  const [retentionStatus, setRetentionStatus] = useState<string | null>(null);

  if (!isAdmin) {
    return <EmptyState title="Administrators only" description="This workspace does not grant you an administrative role." />;
  }

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>Administration</h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        Administrators manage members, shared memory, workspace tools, retention and audit metadata. Private conversations and private
        memory stay invisible: that is enforced in the data layer, not in this screen.
      </p>

      <Card
        title="Plan & Quotas"
        subtitle={
          entitlements.data?.plan
            ? `${entitlements.data.plan.toUpperCase()}${
                entitlements.data.pricing?.priceMonthlyUsd !== undefined && entitlements.data.pricing?.priceMonthlyUsd !== null
                  ? ` · $${entitlements.data.pricing.priceMonthlyUsd}/month`
                  : ""
              }`
            : "unknown"
        }
      >
        <div style={stackStyle("var(--dayvise-space-xs)")}>
          {entitlements.data?.pricing?.trial ? (
            <div
              style={{
                padding: "var(--dayvise-space-xxs) var(--dayvise-space-xs)",
                backgroundColor: "color-mix(in srgb, var(--dayvise-color-accent) 10%, transparent)",
                borderRadius: "var(--dayvise-radius-sm)",
                marginBottom: "var(--dayvise-space-xxs)",
                fontSize: "var(--dayvise-font-size-sm)",
                fontWeight: "var(--dayvise-font-weight-medium)",
              }}
            >
              {entitlements.data.pricing.trial.trialDays}-day free trial with card details (cancel anytime)
            </div>
          ) : null}
          <span style={{ fontSize: "var(--dayvise-font-size-sm)" }}>
            Concurrent runs: {String(entitlements.data?.maxConcurrentRuns ?? "unlimited")}
          </span>
          <span style={{ fontSize: "var(--dayvise-font-size-sm)" }}>
            Retention window ceiling: {String(entitlements.data?.maxRetentionDays ?? 365)} days
          </span>
        </div>
      </Card>

      {/* Member Management */}
      <Card title="Workspace Members" subtitle="Manage team access and roles">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <div style={rowStyle("var(--dayvise-space-sm)", "flex-start")}>
            <TextField label="User ID" value={newUserId} onChange={(e) => setNewUserId(e.target.value)} placeholder="usr_..." />
            <Select
              label="Role"
              options={[
                { value: "member", label: "Member" },
                { value: "admin", label: "Admin" },
                { value: "viewer", label: "Viewer" },
              ]}
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as "admin" | "member" | "viewer")}
            />
            <Button
              variant="primary"
              size="sm"
              loading={adminMutations.addMember.isPending}
              disabled={newUserId.trim().length === 0}
              onClick={() => {
                void adminMutations.addMember
                  .mutateAsync({ userId: newUserId.trim(), role: newRole })
                  .then(() => setNewUserId(""));
              }}
            >
              Add member
            </Button>
          </div>

          {members.isPending ? (
            <Spinner label="Loading members" />
          ) : (
            <div style={stackStyle("var(--dayvise-space-xs)")}>
              {(members.data?.members ?? []).map((m) => (
                <div key={m.membershipId} style={{ ...rowStyle("var(--dayvise-space-sm)", "space-between"), padding: "var(--dayvise-space-xs) 0", borderBottom: "1px solid var(--dayvise-color-border)" }}>
                  <span style={{ fontSize: "var(--dayvise-font-size-sm)" }}>
                    <strong>{m.userId}</strong> ({m.role})
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={adminMutations.removeMember.isPending}
                    onClick={() => void adminMutations.removeMember.mutateAsync(m.userId)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Retention Management */}
      <Card title="Data Retention Policy" subtitle="Enforce bounded storage and prune expired runs">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <p style={{ margin: 0, fontSize: "var(--dayvise-font-size-sm)" }}>
            Data retention runs automatically during scheduled wakeups. You can also trigger a manual sweep now.
          </p>
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Button
              variant="secondary"
              size="sm"
              loading={adminMutations.runRetention.isPending}
              onClick={() => {
                void adminMutations.runRetention.mutateAsync().then((res) => {
                  setRetentionStatus(
                    `Sweep complete: purged ${res.conversations} conversations, ${res.messages} messages, ${res.files} files, ${res.auditEntries} audit entries.`,
                  );
                });
              }}
            >
              Run retention sweep now
            </Button>
          </div>
          {retentionStatus ? (
            <span style={{ color: "var(--dayvise-color-success)", fontSize: "var(--dayvise-font-size-xs)" }}>
              {retentionStatus}
            </span>
          ) : null}
        </div>
      </Card>

      {/* Shared Memory Moderation */}
      <Card title="Shared Memory Moderation" subtitle="Review or purge knowledge shared across the workspace">
        {sharedMemories.isPending ? (
          <Spinner label="Loading shared memories" />
        ) : (sharedMemories.data?.memories ?? []).length === 0 ? (
          <p style={{ margin: 0, fontSize: "var(--dayvise-font-size-sm)", ...MUTED_TEXT }}>
            No shared memories in this workspace.
          </p>
        ) : (
          <div style={stackStyle("var(--dayvise-space-xs)")}>
            {(sharedMemories.data?.memories ?? []).map((mem) => (
              <div key={mem.memoryId} style={{ ...rowStyle("var(--dayvise-space-sm)", "space-between"), padding: "var(--dayvise-space-xs) 0", borderBottom: "1px solid var(--dayvise-color-border)" }}>
                <span style={{ fontSize: "var(--dayvise-font-size-sm)" }}>{mem.content}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={adminMutations.removeSharedMemory.isPending}
                  onClick={() => void adminMutations.removeSharedMemory.mutateAsync(mem.memoryId)}
                >
                  Delete
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Accounts" subtitle="Switching identity clears everything cached for the previous one.">
        <div style={stackStyle("var(--dayvise-space-xs)")}>
          {session.accounts.map((account) => (
            <Button
              key={account.identityId}
              size="sm"
              variant={account.identityId === session.account?.identityId ? "primary" : "secondary"}
              onClick={() => void switchAccount(account)}
            >
              {account.plane === "work" ? "Work" : "Personal"} · {account.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </Card>

      <Card title="Audit metadata" subtitle="Actions, actors and targets. Content is never recorded.">
        {audit.isPending ? (
          <Spinner label="Loading audit metadata" />
        ) : (audit.data?.entries ?? []).length === 0 ? (
          <EmptyState title="No audit entries" description="Administrative actions in this workspace will appear here." />
        ) : (
          <ul style={{ margin: 0, paddingLeft: "var(--dayvise-space-md)", fontSize: "var(--dayvise-font-size-sm)" }}>
            {(audit.data?.entries ?? []).map((entry, index) => (
              <li key={index}>{JSON.stringify(entry)}</li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export type { ScheduleId };
